// External packages
import * as React from 'react';

// Components
import { Select, Option } from 'components/Select';
import { PlainButton } from 'components/Button';
import { Checkbox } from 'components/Checkbox';
import { Icon } from 'components/Icons';

// Modules
import { BulkActionsToolbar } from 'modules/BulkActionsToolbar';
import { VideoModal } from 'modules/videos/VideoModal';

// Hooks
import { useDeleteMedia, useGetAllMedia } from 'api/media';
import { useBulkSelection } from 'hooks/useBulkSelection';

// Types
import type { Media } from '../../../../server/src/types/media.types';

// Utils
import { sortMedia } from 'utils/sortMedia';

export const VideoGallery = () => {
  const [sortBy, setSortBy] = React.useState<string>('datum');
  const [modalOpen, setModalOpen] = React.useState(false);

  const { data: media, isLoading, isError } = useGetAllMedia('videos');
  const { mutate: deleteMedia, isPending: isDeleting } = useDeleteMedia();

  const sortedMedia = React.useMemo(
    () => sortMedia(media, sortBy),
    [media, sortBy]
  );

  const bulkSelection = useBulkSelection(sortedMedia);

  const handleBulkDelete = () => {
    bulkSelection.selectedIds.forEach((id) => {
      deleteMedia({ fileType: 'videos', id });
    });
    bulkSelection.clearSelection();
    setModalOpen(false);
  };

  return (
    <>
      {sortedMedia && sortedMedia.length > 0 && (
        <>
          <Select
            isDisabled={isLoading || isError || !media || media.length === 0}
            placeholder="Sortiraj po"
            wrapperClassName="ml-auto"
            className="w-auto"
            selectedKey={sortBy}
            onSelectionChange={(key) => setSortBy(key as string)}
          >
            <Option id="datum">Dodano (najnovije)</Option>
            <Option id="datum-desc">Dodano (najstarije)</Option>
          </Select>
          <BulkActionsToolbar
            allSelected={!!bulkSelection.allSelected}
            someSelected={!!bulkSelection.someSelected}
            selectedCount={bulkSelection.selectedIds.length}
            onSelectAll={bulkSelection.handleSelectAll}
            onBulkDelete={handleBulkDelete}
            isDeleteModalOpen={modalOpen}
            setDeleteModalOpen={setModalOpen}
            isDeleting={isDeleting}
            label="Odaberi sve"
          />
        </>
      )}

      {isLoading || isError || !sortedMedia || sortedMedia.length === 0 ? (
        <div className="flex items-center justify-center h-full">
          {isLoading ? (
            <Icon name="loader" className="animate-spin size-10" />
          ) : isError ? (
            <p>Greška pri učitavanju video zapisa.</p>
          ) : (
            <p>Nema pronađenih video zapisa.</p>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 overflow-x-auto rounded-lg border border-gray-200 bg-white p-4 lg:grid-cols-4">
          {sortedMedia.map((video: Media) => (
            <div>
              <Checkbox
                isSelected={bulkSelection.selectedIds.includes(video.id)}
                onChange={(checked) =>
                  bulkSelection.handleSelect(video.id, !!checked)
                }
              />
              <VideoModal
                video={video}
                key={video.id}
                modalTrigger={
                  <PlainButton className="cursor-pointer rounded-lg">
                    <div className="overflow-hidden group aspect-image rounded-lg">
                      <video
                        width="320"
                        height="240"
                        muted
                        className="h-full w-full object-cover transition-transform group-hover:scale-110"
                      >
                        <source src={video.url} type="video/mp4" />
                        <source src={video.url} type="video/ogg" />
                        Vaš preglednik ne podržava video tag.
                      </video>
                    </div>
                  </PlainButton>
                }
              />
            </div>
          ))}
        </div>
      )}
    </>
  );
};
